/**
 * ProjectSelectorView -- Selector simple de proyectos (#/projects).
 *
 * Reemplaza la antigua ProjectListView (split sidebar+detail editable, eliminada
 * en CLEANUP-001/T2 por duplicar funcionalidad con la nueva #/projects/:key y
 * por arrastrar el bug PUT id 405 del ApiClient legacy).
 *
 * Responsabilidad unica: listar los proyectos del ecosistema como grid de cards
 * y dejar que el usuario navegue a #/projects/:key (vista detallada con pestanas).
 *
 * Mision: PROJECT-ITEMS-HUB-001-CLEANUP-001 / T2.
 *
 * @module presentation/views/ProjectSelectorView
 */

import { ProjectApiClient } from '../../infrastructure/api/ProjectApiClient.js';
// EL SELLO VIVO va al lado de la version tecleada, no en su lugar
// (PANEL-PROYECTOS-SELLO-VIVO-T1).
import { selloDe, textoDelEstado } from '../../infrastructure/api/SelloVivoClient.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { insigniaHtml } from '../shared/insignia.js';
import { regresoHtml } from '../shared/regreso.js';

const STATUS_COLOR = {
  active: 'var(--accent-green)', operational: 'var(--accent-green)', production: 'var(--accent-green)',
  paused: 'var(--accent-yellow)', beta: 'var(--accent-yellow)', alpha: 'var(--accent-blue)',
  deprecated: 'var(--accent-red)', maintenance: 'var(--text-muted)',
};

function badge(text) {
  const color = STATUS_COLOR[String(text || '').toLowerCase()] || 'var(--text-muted)';
  return insigniaHtml(esc(text || '—'), color, '', 'proj-badge');
}

function pctBar(pct) {
  const v = parseFloat(pct) || 0;
  const c = v >= 70 ? 'var(--accent-green)' : v >= 35 ? 'var(--accent-yellow)' : 'var(--text-muted)';
  return `<div class="proj-progress-bar" title="${v.toFixed(1)}%"><div style="width:${v}%;background:${c}"></div></div>`;
}

// ARGY-DOC-ARCH-AUDIT-001: cobertura de documentacion desde status_metadata.
// El endpoint /Projects (list) ya devuelve statusMetadata (ProjectService.cs).
function docCovOf(p) {
  let meta = {};
  try {
    meta = typeof p.statusMetadata === 'string' ? JSON.parse(p.statusMetadata) : (p.statusMetadata || {});
  } catch { meta = {}; }
  return (meta && (meta.doc_coverage || meta.docCoverage)) || null;
}

// Regla rollup (v2, #2434): un proyecto cuenta como alineado si carpeta_principal_alineada
// === true (booleano estricto). Valores string ("PR ...") = pendiente, NO cuentan.
function isCarpetaAlineada(dc) {
  return !!dc && dc.carpeta_principal_alineada === true;
}
// Alineacion pendiente: carpeta_principal_alineada es un string (referencia a un PR/accion).
function isAlineadaPendiente(dc) {
  return !!dc && typeof dc.carpeta_principal_alineada === 'string' && dc.carpeta_principal_alineada.trim() !== '';
}

// % documentado defensivo (pct_documentado directo o derivado docs_en_center/docs_md).
function docPct(dc) {
  if (!dc) return null;
  if (dc.pct_documentado != null && dc.pct_documentado !== '') return Number(dc.pct_documentado) || 0;
  const md = Number(dc.docs_md) || 0;
  const center = Number(dc.docs_en_center ?? dc.docs_mapeados_center) || 0;
  return md > 0 ? (center / md) * 100 : null;
}

function docChip(dc) {
  const pct = docPct(dc);
  if (pct == null) return '';
  const c = pct >= 70 ? 'var(--accent-green)' : pct >= 35 ? 'var(--accent-yellow)' : 'var(--text-muted)';
  // PROYECTOS-ALINEADOS-T1: dice lo que mide (docs .md copiados a Center, auditoria a mano) para no leerse como contratos.
  const cuando = dc.ultima_auditoria ? `auditoria del ${dc.ultima_auditoria}` : 'auditoria sin fecha';
  return insigniaHtml(`&#128218; docs en Center ${pct.toFixed(0)}%`, c, `title="Docs .md del repo copiados a Nexo Center (${esc(cuando)}); no son los contratos de los nodos"`, 'proj-badge');
}

export async function renderProjectSelector(container) {
  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Proyectos del Ecosistema</h1>
          <p style="font-size:0.875rem;color:var(--text-secondary);margin-top:2px">
            Selecciona un proyecto para ver canon, items vinculados, misiones, bitacora y estado operacional.
          </p>
        </div>
      </div>

      <div id="proj-sel-rollup" style="margin-top:12px"></div>

      <div id="proj-sel-grid" class="proj-sel-grid">
        <div class="empty-state">Cargando proyectos…</div>
      </div>
    </section>

    <style>
      .proj-sel-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 12px;
        margin-top: 14px;
      }
      .proj-sel-card {
        display: block;
        text-decoration: none;
        color: inherit;
        background: var(--bg-secondary);
        border: 1px solid var(--bg-tertiary);
        border-radius: 8px;
        padding: 14px;
        transition: border-color .12s, transform .12s;
      }
      .proj-sel-card:hover {
        border-color: var(--accent-blue);
        transform: translateY(-1px);
      }
      .proj-sel-card-title {
        font-size: .98rem;
        font-weight: 600;
        color: var(--text-heading);
        margin-bottom: 4px;
      }
      .proj-sel-card-key {
        font-size: .875rem;
        color: var(--text-secondary);
        font-family: ui-monospace, SFMono-Regular, monospace;
      }
      .proj-sel-card-meta {
        display: flex;
        gap: 6px;
        align-items: center;
        margin-top: 8px;
        flex-wrap: wrap;
        font-size: .875rem;
      }
      .proj-sel-card-progress {
        margin-top: 10px;
      }
      .proj-sel-card-desc {
        font-size: .875rem;
        color: var(--text-secondary);
        margin-top: 8px;
        line-height: 1.4;
        max-height: 3.4em;
        overflow: hidden;
      }
    </style>`;

  const $grid = container.querySelector('#proj-sel-grid');

  const res = await ProjectApiClient.list(1, 50);
  if (!res.ok) {
    $grid.innerHTML = `<p class="empty-state">Error al cargar proyectos: ${esc(res.error || 'desconocido')}</p>`;
    return;
  }

  const payload = res.data?.data ?? res.data;
  const items   = payload?.items ?? [];

  if (!items.length) {
    $grid.innerHTML = '<p class="empty-state">Sin proyectos registrados en <code>nexo_catalogs.projects</code>.</p>';
    return;
  }

  // Rollup global (v2, #2434): X/N proyectos con carpeta principal alineada en Center
  // (carpeta_principal_alineada === true). Los que tienen un string ("PR ...") = pendiente.
  const $rollup = container.querySelector('#proj-sel-rollup');
  if ($rollup) {
    const total = items.length;
    const dcs = items.map(p => docCovOf(p));
    const aligned = dcs.filter(isCarpetaAlineada).length;
    const pending = dcs.filter(isAlineadaPendiente).length;
    const pct = total ? (aligned / total) * 100 : 0;
    const c = pct >= 70 ? 'var(--accent-green)' : pct >= 35 ? 'var(--accent-yellow)' : 'var(--text-muted)';
    $rollup.innerHTML = `
      <div style="padding:12px 14px;background:var(--bg-secondary);border:1px solid var(--bg-tertiary);border-radius:8px">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">
          <div style="font-size:.875rem;color:var(--text-heading)">
            &#128218; <strong>Carpeta principal alineada en Nexo Center:</strong>
            <span style="color:${c};font-weight:700">${aligned}/${total}</span> proyectos
          </div>
          <div style="min-width:180px;flex:1;max-width:320px">${pctBar(pct)}</div>
        </div>
        ${pending > 0 ? `<div style="font-size:.875rem;color:var(--accent-yellow);margin-top:6px">&#128993; ${pending} proyecto(s) con alineacion <strong>pendiente</strong> (referenciados a un PR en curso).</div>` : ''}
      </div>`;
  }

  $grid.innerHTML = items.map(p => {
    const key   = p.projectKey || '';
    const name  = p.displayName || key || '(sin nombre)';
    const stat  = p.operationalStatus || p.status || '';
    const ver   = p.currentVersion || '';
    const desc  = p.description || '';
    const progress = parseFloat(p.progressPercent || 0).toFixed(0);
    const dcChip = docChip(docCovOf(p));

    return `
      <a href="#/projects/${encodeURIComponent(key)}" class="proj-sel-card" aria-label="Abrir proyecto ${esc(name)}">
        <div class="proj-sel-card-title">${esc(name)}</div>
        <div class="proj-sel-card-key">
          <code>${esc(key)}</code>${ver ? ` · <span title="version escrita a mano en la ficha del proyecto">anotada v${esc(ver)}</span>` : ''}
          <span class="proj-sel-sello" data-sello="${esc(key)}" title="midiendo el sello del sitio">· midiendo...</span>
        </div>
        <div class="proj-sel-card-meta">${badge(stat)} <span style="color:var(--text-secondary)">${progress}%</span>${dcChip ? ` ${dcChip}` : ''}</div>
        <div class="proj-sel-card-progress">${pctBar(p.progressPercent)}</div>
        ${desc ? `<div class="proj-sel-card-desc">${esc(desc)}</div>` : ''}
      </a>`;
  }).join('');

  pintarSellos($grid);
}

/**
 * PREGUNTA EL SELLO DE CADA TARJETA Y LO PINTA CUANDO LLEGA.
 *
 * Va DESPUES de pintar y no antes: la tarjeta tiene que verse aunque los
 * sellos tarden o no contesten. Mientras llega dice -midiendo...-, que no es
 * lo mismo que un numero viejo puesto como si fuera de ahora.
 *
 * Y cuando no se puede leer, la tarjeta dice NO MEDIDO y el porque queda en
 * el title: -no contesta- y -contesta pero no nos deja leerlo- son cosas
 * distintas, y confundirlas manda a buscar una caida que no existe.
 */
async function pintarSellos($grid) {
  const $marcas = [...$grid.querySelectorAll('[data-sello]')];
  await Promise.all($marcas.map(async ($m) => {
    const sello = await selloDe($m.dataset.sello);
    const servido = sello.estado === 'servido';
    $m.textContent = '· ' + textoDelEstado(sello);
    $m.style.color = servido ? 'var(--accent-green-text)' : 'var(--text-secondary)';
    $m.title = servido
      ? 'lo que el sitio esta sirviendo ahora' + (sello.commit ? ', commit ' + sello.commit : '')
      : 'NO MEDIDO: ' + sello.porque;
  }));
}
